import { useLandingLanguage } from './LandingLanguage';
import React, { createContext, useContext, useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Dialog, DialogActions, DialogContent, DialogTitle, Button, FormControlLabel, Switch } from '@mui/material';
import { suspendAnalytics, trackPublicPage } from '../../services/analytics';
const storageKey = 'orarivo-cookie-consent-v2';
const maxAge = 180 * 86400000;
type Consent = { version: 2; savedAt: number; analytics: boolean };
const readConsent = (): Consent | null => {
  try {
    const value = JSON.parse(localStorage.getItem(storageKey) || 'null');
    return value?.version === 2 && typeof value.analytics === 'boolean' && Number.isFinite(value.savedAt) && value.savedAt <= Date.now() && Date.now() - value.savedAt < maxAge ? value : null;
  } catch { return null; }
};
const CookieContext = createContext({ openNotice: () => {} });
export const useCookieNotice = () => useContext(CookieContext);
export const CookieNoticeProvider = ({ children }: { children: React.ReactNode }) => {
  const { t } = useLandingLanguage();
  const { pathname } = useLocation();
  const [consent, setConsent] = useState<Consent | null>(readConsent);
  const [open, setOpen] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [saved, setSaved] = useState(true);
  const openNotice = () => { setAnalytics(consent?.analytics === true); setOpen(true); };
  const save = (allowed: boolean) => {
    const value: Consent = { version: 2, savedAt: Date.now(), analytics: allowed };
    try { localStorage.setItem(storageKey, JSON.stringify(value)); setSaved(true); } catch { setSaved(false); }
    if (!allowed) suspendAnalytics(true);
    setConsent(value); setAnalytics(allowed); setOpen(false);
  };
  useEffect(() => {
    if (consent?.analytics) trackPublicPage(pathname);
    else suspendAnalytics(true);
  }, [consent, pathname]);
  useEffect(() => {
    const sync = (event: StorageEvent) => {
      if (event.key === storageKey || event.key === null) setConsent(readConsent());
    };
    window.addEventListener('storage', sync);
    return () => window.removeEventListener('storage', sync);
  }, []);
  useEffect(() => {
    if (!consent) return;
    let timer: ReturnType<typeof setTimeout>;
    const check = () => {
      const remaining = consent.savedAt + maxAge - Date.now();
      if (remaining <= 0) { suspendAnalytics(true); setConsent(null); }
      else timer = setTimeout(check, Math.min(remaining, 2147483647));
    };
    check();
    return () => clearTimeout(timer);
  }, [consent]);
  return <CookieContext.Provider value={{ openNotice }}>{children}
    {!consent && <aside className="orarivo-cookie-banner" aria-label={t('Informații despre cookies și stocare locală')}><div><strong>{t('Tu alegi ce cookies folosim.')}</strong><p>{t('Folosim stocare necesară pentru autentificare și preferințe. Cu acordul tău, Google Analytics ne ajută să înțelegem folosirea paginilor publice.')} <Link to="/cookies/">{t('Vezi politica cookies')}</Link>.</p></div><div className="orarivo-cookie-actions"><button type="button" onClick={() => save(false)}>{t('Doar necesare')}</button><button type="button" onClick={openNotice}>{t('Detalii și preferințe')}</button><button type="button" onClick={() => save(true)}>{t('Acceptă toate')}</button></div></aside>}
    {!saved && <aside className="orarivo-cookie-save-warning" role="status">{t('Browserul a blocat salvarea preferinței. Alegerea se aplică acestei vizite și poate fi cerută din nou.')} <button type="button" onClick={openNotice}>{t('Preferințe cookies')}</button></aside>}
    <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm" aria-labelledby="cookie-title">
      <DialogTitle id="cookie-title">{t('Cookies și stocare locală')}</DialogTitle>
      <DialogContent><div className="orarivo-cookie-details"><h3>{t('Strict necesare · mereu active')}</h3><p>{t('Stocarea pentru autentificare, limbă și preferințele cookies este necesară funcționării site-ului.')}</p><h3>Google Analytics</h3><p>{t('Opțional. Măsoară vizitele paginilor publice pentru a îmbunătăți site-ul. Nu trimitem datele din conturi, formulare, programări sau documente și nu activăm publicitate.')}</p><FormControlLabel control={<Switch checked={analytics} onChange={event => setAnalytics(event.target.checked)} />} label={t('Permite Google Analytics')}/><p>{t('Poți schimba alegerea oricând din „Preferințe cookies” din subsol. Refuzul Analytics nu afectează accesul la aplicație.')}</p><Link to="/cookies/" onClick={() => setOpen(false)}>{t('Citește politica completă')}</Link></div></DialogContent>
      <DialogActions sx={{ flexWrap: 'wrap', gap: 1, px: 3, pb: 2 }}><Button onClick={() => save(false)}>{t('Doar necesare')}</Button><Button onClick={() => save(analytics)}>{t('Salvează preferințele')}</Button><Button onClick={() => save(true)} variant="contained">{t('Acceptă toate')}</Button></DialogActions>
    </Dialog>
  </CookieContext.Provider>;
};
